import { IShipmentFormInput, schema } from '../schema'

export interface ValidationError {
  id: string
  message: string
  severity: 'error' | 'warning'
  step?: number
  dataGroup?: string
}

export const validateTemperatureControlAlignment = (
  data: IShipmentFormInput
): ValidationError[] => {
  const errors: ValidationError[] = []

  if (!data.bookings) return errors

  data.bookings.forEach((booking, bookingIndex) => {
    if (!booking.packaging) return

    booking.packaging.forEach((packaging, packagingIndex) => {
      const isLoadTemperatureControlled = packaging.temperatureControlled
      const loadTemperatureRange = packaging.temperatureRange

      if (packaging.products && Array.isArray(packaging.products)) {
        let hasTemperatureControlledProduct = packaging.products.length === 0

        packaging.products.forEach((product, productIndex) => {
          const isProductTemperatureControlled = product.temperatureControlled
          const productTemperatureRange = product.temperatureSetting?.temperatureRangeId

          if (isProductTemperatureControlled) {
            hasTemperatureControlledProduct = true
          }

          if (!isLoadTemperatureControlled && isProductTemperatureControlled) {
            errors.push({
              id: `temp-mismatch-${bookingIndex}-${packagingIndex}-${productIndex}`,
              message: `Consignment #${bookingIndex + 1}, Load #${
                packagingIndex + 1
              }: Non-temperature controlled load contains temperature controlled content. Please either enable temperature control for the load or remove temperature control from the content.`,
              severity: 'error',
              step: 3,
              dataGroup: 'Load Contents',
            })
          }

          if (
            isLoadTemperatureControlled &&
            isProductTemperatureControlled &&
            loadTemperatureRange &&
            productTemperatureRange &&
            loadTemperatureRange !== productTemperatureRange
          ) {
            errors.push({
              id: `temp-range-mismatch-${bookingIndex}-${packagingIndex}-${productIndex}`,
              message: `Consignment #${bookingIndex + 1}, Load #${
                packagingIndex + 1
              }: Temperature range mismatch. Load temperature range does not match content temperature range.`,
              severity: 'error',
              step: 3,
              dataGroup: 'Load Contents',
            })
          }
        })

        if (isLoadTemperatureControlled && !hasTemperatureControlledProduct) {
          errors.push({
            id: `temp-missing-${bookingIndex}-${packagingIndex}`,
            message: `Consignment #${bookingIndex + 1}, Load #${
              packagingIndex + 1
            }: Temperature controlled load must contain at least one temperature controlled content item.`,
            severity: 'error',
            step: 3,
            dataGroup: 'Load Contents',
          })
        }
      }
    })
  })

  return errors
}

export const validateScheduledDates = async (
  data: IShipmentFormInput
): Promise<ValidationError[]> => {
  const errors: ValidationError[] = []

  const result = await schema.safeParseAsync(data)

  if (!result.success) {
    const pastDateErrorsByBooking = new Map<number, boolean>()

    result.error.errors.forEach((zodError: any) => {
      const path = zodError.path.join('.')

      if (
        path.includes('loadingDate') ||
        path.includes('deliveryDate') ||
        path.includes('scheduledDate')
      ) {
        const bookingIndex = extractBookingIndexFromPath(zodError.path)
        const fieldName = getFieldNameFromPath(zodError.path)

        if (zodError.message.includes('cannot be in the past')) {
          if (!pastDateErrorsByBooking.has(bookingIndex)) {
            pastDateErrorsByBooking.set(bookingIndex, true)
            errors.push({
              id: `schema-date-error-${bookingIndex}-past-date`,
              message: `Consignment #${bookingIndex + 1}: Scheduled date cannot be in the past.`,
              severity: 'error',
              step: 1,
              dataGroup: 'Shipment Information',
            })
          }
        } else {
          errors.push({
            id: `schema-date-error-${bookingIndex}-${fieldName}`,
            message: `Consignment #${bookingIndex + 1}: Scheduled date cannot be in the past.`,
            severity: 'error',
            step: 1,
            dataGroup: 'Shipment Information',
          })
        }
      }
    })
  }

  return errors
}

const extractBookingIndexFromPath = (path: (string | number)[]): number => {
  const bookingIndex = path.findIndex(
    (segment, index) => segment === 'bookings' && typeof path[index + 1] === 'number'
  )
  return bookingIndex >= 0 ? (path[bookingIndex + 1] as number) : 0
}

const getFieldNameFromPath = (path: (string | number)[]): string => {
  const lastSegment = path[path.length - 1]
  if (lastSegment === 'loadingDate') return 'loading'
  if (lastSegment === 'deliveryDate') return 'delivery'
  if (lastSegment === 'scheduledDate') return 'scheduled'
  return 'date'
}

export const validateShipmentData = async (
  data: IShipmentFormInput
): Promise<ValidationError[]> => {
  const temperatureErrors = validateTemperatureControlAlignment(data)
  const dateErrors = await validateScheduledDates(data)

  return [...temperatureErrors, ...dateErrors]
}
